import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowDown, ArrowUpRight } from "lucide-react";
import { KineticLine, Reveal, ProductCard, PartnerForm } from "@/components/Motion";
import Marquee from "@/components/Marquee";
import { useVideo } from "@/components/VideoModal";
import { API, IMAGES, MARQUEE_ITEMS, COMPLIANCE } from "@/data/site";

const SCIENCE_POINTS = [
  { n: "01", t: "Cellular communication", d: "Exosomes naturally transport biological signals between cells." },
  { n: "02", t: "Nano-scale delivery", d: "Their small size and lipid structure make them natural carriers for bio-active compounds." },
  { n: "03", t: "Skin & scalp research", d: "Studied for their relationship to repair signaling, collagen pathways and barrier comfort." },
  { n: "04", t: "Professional interest", d: "Regenerative aesthetics demands products that support visible recovery, hydration and firmness." },
  { n: "05", t: "Cosmetic positioning", d: "Our formulas support appearance — they are cosmetics, not approved therapeutic biologics." },
];

const PILLARS = [
  { t: "Exclusive market territory", d: "Protected regional rights and priority access to our 2026 product portfolio." },
  { t: "Comprehensive brand empowerment", d: "Dedicated professional support, standardized application guidelines and premium marketing resources." },
  { t: "Optimized profit model", d: "Competitive margins and strategic incentive programs for sustainable growth." },
];

export default function Home() {
  const location = useLocation();
  const { openVideo } = useVideo();
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const ringY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" }), 250);
    }
  }, [location]);

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then((r) => r.json())
      .then(setProducts)
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-16" data-testid="hero-section">
        <div className="lab-grid pointer-events-none absolute inset-0 opacity-60" />
        <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-5 pb-16 pt-12 md:px-10 lg:grid-cols-12 lg:pb-24 lg:pt-20">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="font-lab mb-8 inline-flex items-center gap-3 border border-[#0e8c8c]/30 bg-[#0e8c8c]/5 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#0e8c8c]" />
              Exoplus Bio · Clinical exosome science
            </motion.p>

            <h1
              className="font-display text-[clamp(2.7rem,7vw,5.6rem)] leading-[0.98] tracking-tight"
              data-testid="hero-headline"
            >
              <KineticLine delay={0.1}>The future of</KineticLine>
              <KineticLine delay={0.22}>
                aesthetic care, <em className="text-[#0e8c8c]">within</em>
              </KineticLine>
              <KineticLine delay={0.34}>
                <em>your reach.</em>
              </KineticLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="mt-8 max-w-md text-base leading-relaxed text-[#56636b]"
              data-testid="hero-subline"
            >
              Advanced cosmetic formulas designed to support healthy-looking skin and hair — pairing exosome signaling
              with precision delivery. Elevate your aesthetics. Trust in science.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={() => document.querySelector("#collection")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-cta-collection"
                className="group inline-flex items-center gap-3 bg-[#0a0a0a] px-8 py-4 text-sm font-medium text-white transition-colors hover:bg-[#0e8c8c]"
              >
                Explore the collection
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
              </button>
              <button
                onClick={() => openVideo()}
                data-testid="hero-cta-film"
                className="group inline-flex items-center gap-3 border border-[#e4e9ec] bg-white/60 px-7 py-4 text-sm font-medium transition-colors hover:border-[#0a0a0a]"
              >
                <Play className="h-4 w-4 text-[#0e8c8c]" /> Watch the film
              </button>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="relative overflow-hidden border border-[#e4e9ec]"
                data-testid="hero-visual"
              >
                <img
                  src={IMAGES.hero}
                  alt="Extracellular vesicles suspended in clinical serum"
                  className="aspect-[4/5] w-full object-cover"
                />
                <span className="font-lab absolute bottom-4 left-4 bg-white/85 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#0a0a0a] backdrop-blur-sm">
                  Fig. 01 — Extracellular vesicles, 30–150 nm
                </span>
              </motion.div>
              <motion.div
                style={{ y: ringY }}
                className="animate-spin-slow pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full border border-dashed border-[#0e8c8c]/50 lg:-right-14"
              />
            </motion.div>
          </div>
        </div>

        {/* STATS */}
        <div className="relative border-t border-[#e4e9ec]" data-testid="hero-stats">
          <div className="mx-auto grid max-w-[1440px] grid-cols-2 md:grid-cols-4">
            {[
              { v: "5", l: "Products in the platform" },
              { v: "2.1T", l: "Peak vesicles per application" },
              { v: "4", l: "Delivery technologies" },
              { v: "CNF", l: "Notified — Health Canada" },
            ].map((s, i) => (
              <div key={i} className="border-r border-[#e4e9ec] px-6 py-8 last:border-r-0 md:px-10" data-testid={`hero-stat-${i}`}>
                <p className="font-display text-3xl md:text-4xl">{s.v}</p>
                <p className="font-lab mt-2 text-[10px] uppercase tracking-[0.16em] text-[#56636b]">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* SCIENCE */}
      <section id="science" className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32" data-testid="science-section">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <Reveal className="md:col-span-7">
            <div className="overflow-hidden border border-[#e4e9ec]">
              <img src={IMAGES.science} alt="Microscopic view of lipid vesicles" className="aspect-[16/10] w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          </Reveal>
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">The science</p>
              <h2 className="font-display mt-4 text-4xl leading-tight tracking-tight md:text-5xl">
                Nature's messengers, studied in the lab
              </h2>
              <p className="mt-6 text-base leading-relaxed text-[#56636b]">
                Exosomes are microscopic extracellular vesicles — commonly described in the 30–150 nanometre range.
                Enclosed by a lipid membrane, they carry proteins, lipids and genetic material between cells. Their
                core biological role is communication, which is why cosmetic science is so interested in the signaling
                concept.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 border-l-2 border-[#0e8c8c] bg-[#e3f2f0]/40 p-5" data-testid="science-note">
                <p className="text-sm leading-relaxed text-[#0a0a0a]">
                  <span className="font-lab text-[10px] uppercase tracking-[0.16em] text-[#0e8c8c]">A note on biologics — </span>
                  Exosomes are studied well beyond cosmetics. Cosmetic products should not be confused with approved
                  therapeutic biologics or drug treatments.
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {SCIENCE_POINTS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.07}>
              <div className="group h-full border border-[#e4e9ec] bg-white p-6 transition-colors duration-300 hover:border-[#0a0a0a]" data-testid={`science-point-${p.n}`}>
                <span className="font-lab text-[10px] tracking-[0.2em] text-[#0e8c8c]">{p.n}</span>
                <h3 className="font-display mt-4 text-lg leading-snug">{p.t}</h3>
                <p className="mt-3 text-[13px] leading-relaxed text-[#56636b]">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COLLECTION */}
      <section id="collection" className="border-t border-[#e4e9ec] py-24 md:py-32" data-testid="collection-section">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">The Vantis Collection</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="max-w-sm text-sm leading-relaxed text-[#56636b]">
                Daily home care, concentrated professional formulations and precision delivery — each product occupies
                a clear role in a coherent bio-aesthetic system.
              </p>
              <p className="mt-4 max-w-sm border border-[#0e8c8c]/25 bg-[#e3f2f0]/40 p-4 text-[13px] leading-relaxed text-[#0a0a0a]" data-testid="distributor-note-home">
                <span className="font-lab text-[10px] uppercase tracking-[0.16em] text-[#0e8c8c]">For distributors — </span>
                connect with your local representative to place your order. No assigned rep?{" "}
                <a href="#partner" data-testid="distributor-note-home-link" className="font-medium text-[#0e8c8c] underline underline-offset-4">
                  Fill out our contact form
                </a>{" "}
                and someone will connect with you shortly.
              </p>
            </Reveal>
          </div>

          {products.length === 0 ? (
            <p className="font-lab text-xs uppercase tracking-[0.16em] text-[#56636b]">Loading the collection…</p>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {products.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PARTNER */}
      <section id="partner" className="grain lab-grid-dark relative overflow-hidden bg-[#070b0c] py-24 text-white md:py-32" data-testid="partner-section">
        <div className="relative z-10 mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-5 md:px-10 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Partner with us</p>
              <h2 className="font-display mt-4 text-4xl leading-tight tracking-tight md:text-5xl">
                For more information
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
                Join us in bringing innovative cosmetic solutions to markets that help shape the future of skin and
                hair care.
              </p>
            </Reveal>
            <div className="mt-12 space-y-0 border-t border-white/10">
              {PILLARS.map((p, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="flex gap-6 border-b border-white/10 py-6" data-testid={`partner-pillar-${i}`}>
                    <span className="font-lab text-[10px] tracking-[0.2em] text-[#0e8c8c]">0{i + 1}</span>
                    <div>
                      <h3 className="font-display text-xl">{p.t}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-white/55">{p.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15}>
            <div className="border border-white/10 bg-white/5 p-8 backdrop-blur-sm md:p-10">
              <h3 className="font-display text-2xl">Partner with us</h3>
              <p className="mt-2 text-sm text-white/50">Our team responds within two business days.</p>
              <div className="mt-8">
                <PartnerForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="py-20" data-testid="compliance-section">
        <div className="mx-auto max-w-3xl px-5 text-center md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Responsible communication</p>
            <p className="font-display mt-6 text-xl italic leading-relaxed text-[#0a0a0a] md:text-2xl">
              "{COMPLIANCE.split("unless explicitly stated otherwise")[0]}… unless explicitly stated otherwise."
            </p>
            <p className="mt-6 text-sm leading-relaxed text-[#56636b]">
              Individual results may vary. These products are intended for cosmetic use to enhance the appearance of
              skin and hair and are not intended to diagnose, treat, cure, or prevent any disease or medical condition.
            </p>
            <a
              href="#collection"
              data-testid="compliance-cta"
              className="font-lab mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0a0a0a] underline-offset-4 hover:underline"
            >
              Explore the collection <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}